'use client';

import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import type { CmsFooterLocaleSwitcherModel } from '@/cms/components/chrome/cms-footer-locale-switcher.types';
import type { CmsSiteChromeEditTargetAttributes } from '@/cms/components/chrome/cms-site-chrome-target';
import {
  type CmsSiteFooterModel,
  CmsSiteFooterView,
} from '@/cms/components/chrome/cms-site-footer-view';
import { CmsSiteHeaderView } from '@/cms/components/chrome/cms-site-header-view';
import type {
  CmsSiteHeaderModel,
  CmsSiteHeaderNavigationItem,
  CmsSiteHeaderNavigationNode,
} from '@/cms/components/chrome/cms-site-header-view';
import headerStyles from '@/cms/components/chrome/cms-site-header.module.css';
import { X } from '@/components/icons/app-icons';
import {
  dismissBannerForSession,
  isBannerDismissedInSession,
} from '@/site-runtime/announcement-session';
import {
  getSiteRuntimeFooterLegalLinks,
  getSiteRuntimeNavigationBadge,
  getSiteRuntimeNavigationIcon,
  getSiteRuntimeNavigationTarget,
} from '@/site-runtime/navigation';
import type { SiteRuntimeTarget } from '@/site-runtime/runtime-config-contract';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  SiteRuntimeBanner,
  SiteRuntimeGlobalSnapshot,
  SiteRuntimeNavigationItem,
  SiteRuntimeNavigationLeaf,
  SiteRuntimeNavigationNode,
  SiteRuntimeNavigationSurface,
  SiteRuntimeUiCopy,
} from '../types';
import {
  type SiteRuntimeElementDataAttributes,
  SiteRuntimeTargetTrigger,
} from './site-runtime-entry-action-controller';

export type SiteRuntimeHeaderEditTargets = {
  accountLogin: CmsSiteChromeEditTargetAttributes;
  accountPrimaryCta: CmsSiteChromeEditTargetAttributes;
  brand: CmsSiteChromeEditTargetAttributes;
  navigationItem: (
    path: readonly number[]
  ) => CmsSiteChromeEditTargetAttributes | undefined;
};

export type SiteRuntimeFooterEditTargets = NonNullable<
  CmsSiteFooterModel['editTargets']
>;

export type SiteRuntimeBannerEditTargets = {
  cta: SiteRuntimeElementDataAttributes;
  dismiss: SiteRuntimeElementDataAttributes;
  marker: SiteRuntimeElementDataAttributes;
  message: SiteRuntimeElementDataAttributes;
};

function runtimeTarget(target: SiteRuntimeTarget) {
  return {
    kind: 'runtime-target' as const,
    target,
  };
}

function runtimeIcon(
  link: SiteRuntimeNavigationNode,
  surface: SiteRuntimeNavigationSurface
) {
  const icon = getSiteRuntimeNavigationIcon(link, surface);
  if (icon?.iconSource !== 'app') {
    return undefined;
  }

  return {
    kind: 'app' as const,
    name: icon.iconKey,
  };
}

function buildRuntimeNavigationBase(
  link: SiteRuntimeNavigationItem | SiteRuntimeNavigationLeaf,
  editPath: readonly number[]
): Omit<CmsSiteHeaderNavigationNode, 'children'> {
  return {
    badge: getSiteRuntimeNavigationBadge(link, 'header'),
    editPath,
    icon: runtimeIcon(link, 'header'),
    label: link.label,
    target: runtimeTarget(getSiteRuntimeNavigationTarget(link, 'header')),
  };
}

function buildRuntimeNavigationNode(
  link: SiteRuntimeNavigationLeaf,
  editPath: readonly number[]
): CmsSiteHeaderNavigationNode {
  return {
    ...buildRuntimeNavigationBase(link, editPath),
    children: [],
  };
}

function buildRuntimeHeaderNavigation(
  global: SiteRuntimeGlobalSnapshot
): CmsSiteHeaderNavigationItem[] {
  const links = global.shell.navigation.items.filter(
    (item) => item.surfaces.header
  );

  return links.map((link, index) => ({
    ...buildRuntimeNavigationBase(link, [index]),
    columns: link.childGroups.map((column, columnIndex) => {
      return {
        editPath: [index, columnIndex],
        title: column.title,
        links: column.links.map((columnLink, linkIndex) =>
          buildRuntimeNavigationNode(columnLink, [
            index,
            columnIndex,
            linkIndex,
          ])
        ),
      };
    }),
    ...(link.childGroups.length > 0
      ? { dataAttributes: { 'data-runtime-header-menu': link.label } }
      : {}),
  }));
}

function buildRuntimeHeaderModel(
  global: SiteRuntimeGlobalSnapshot,
  editTargets: SiteRuntimeHeaderEditTargets | undefined,
  viewerMode: CmsShellViewerMode,
  currentPath: string,
  locale: string
): CmsSiteHeaderModel {
  return {
    account: {
      actionMode: 'runtime',
      hasWorkspaceTarget: true,
      primaryCtaLabel: global.shell.header.account.primaryCta?.label,
      primaryCtaTarget: global.shell.header.account.primaryCta?.target,
      utilityLabel: global.shell.header.account.loginLabel,
      viewerMode,
    },
    brand: {
      label: global.shell.header.brand.name?.trim() ?? '',
      target: runtimeTarget(global.shell.header.brand.homeTarget),
    },
    currentPath,
    editTargets,
    locale,
    navigation: buildRuntimeHeaderNavigation(global),
    uiCopy: {
      closeNavigation: global.uiCopy.closeNavigation,
      mobileNavigation: global.uiCopy.mobileNavigation,
      openNavigation: global.uiCopy.openNavigation,
      primaryNavigation: global.uiCopy.primaryNavigation,
    },
  };
}

function buildRuntimeFooterModel(
  global: SiteRuntimeGlobalSnapshot,
  localeSwitcherModel: CmsFooterLocaleSwitcherModel,
  editTargets: SiteRuntimeFooterEditTargets | undefined
): CmsSiteFooterModel {
  const toFooterLink = (link: SiteRuntimeNavigationNode) => ({
    badge: getSiteRuntimeNavigationBadge(link, 'footer'),
    label: link.label,
    target: runtimeTarget(getSiteRuntimeNavigationTarget(link, 'footer')),
  });
  const footerGroups = global.shell.navigation.items.filter(
    (item) => item.surfaces.footer === 'group'
  );
  const socialLinks = global.shell.navigation.items.filter(
    (item) => item.surfaces.footer === 'social'
  );

  return {
    brandLabel: global.shell.header.brand.name?.trim() ?? '',
    copyright: global.shell.footer.copyright,
    editTargets,
    groups: footerGroups.map((group) => ({
      label: group.label,
      links: group.childGroups.flatMap((childGroup) =>
        childGroup.links
          .filter(
            (link) =>
              getSiteRuntimeNavigationTarget(link, 'footer').type === 'open_url'
          )
          .map(toFooterLink)
      ),
    })),
    legalLinks: getSiteRuntimeFooterLegalLinks(global.shell).map(toFooterLink),
    localeSwitcherModel,
    socialLinks: socialLinks.map((link) => ({
      icon: runtimeIcon(link, 'footer'),
      label: link.label,
      target: runtimeTarget(getSiteRuntimeNavigationTarget(link, 'footer')),
    })),
  };
}

export function SiteRuntimeHeader({
  currentPath,
  editTargets,
  global,
  locale,
  viewerMode = 'live',
}: {
  currentPath: string;
  editTargets?: SiteRuntimeHeaderEditTargets;
  global: SiteRuntimeGlobalSnapshot;
  locale: string;
  viewerMode?: CmsShellViewerMode;
}) {
  const model = buildRuntimeHeaderModel(
    global,
    editTargets,
    viewerMode,
    currentPath,
    locale
  );
  return <CmsSiteHeaderView model={model} />;
}

export function SiteRuntimeFooter({
  editTargets,
  global,
  localeSwitcherModel,
}: {
  editTargets?: SiteRuntimeFooterEditTargets;
  global: SiteRuntimeGlobalSnapshot;
  localeSwitcherModel: CmsFooterLocaleSwitcherModel;
}) {
  return (
    <CmsSiteFooterView
      model={buildRuntimeFooterModel(global, localeSwitcherModel, editTargets)}
    />
  );
}

export function SiteRuntimeBannerView({
  banner,
  editTargets,
  uiCopy,
}: {
  banner: SiteRuntimeBanner;
  editTargets?: SiteRuntimeBannerEditTargets;
  uiCopy: SiteRuntimeUiCopy;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setDismissed(isBannerDismissedInSession(banner));
    if (document.readyState === 'complete') {
      setPageLoaded(true);
      return undefined;
    }

    const showBannerAfterPageLoad = () => setPageLoaded(true);
    window.addEventListener('load', showBannerAfterPageLoad, { once: true });
    return () => window.removeEventListener('load', showBannerAfterPageLoad);
  }, [banner]);

  useLayoutEffect(() => {
    if (!pageLoaded || dismissed || !bannerRef.current) {
      document.documentElement.style.removeProperty('--cms-site-banner-offset');
      return undefined;
    }

    const updateBannerOffset = () => {
      const height = bannerRef.current?.getBoundingClientRect().height ?? 0;
      document.documentElement.style.setProperty(
        '--cms-site-banner-offset',
        `${height}px`
      );
    };

    updateBannerOffset();
    const resizeObserver = new ResizeObserver(updateBannerOffset);
    resizeObserver.observe(bannerRef.current);
    window.addEventListener('resize', updateBannerOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateBannerOffset);
      document.documentElement.style.removeProperty('--cms-site-banner-offset');
    };
  }, [dismissed, pageLoaded]);

  if (dismissed || !pageLoaded) {
    return null;
  }

  return (
    <div
      className={headerStyles.siteBanner}
      data-site-runtime-chrome="banner"
      ref={bannerRef}
    >
      <SiteRuntimeTargetTrigger
        className={headerStyles.siteBannerLink}
        target={banner.target}
      >
        <span className={headerStyles.siteBannerInner}>
          <span className={headerStyles.siteBannerMarkerSlot}>
            {banner.marker.type === 'emoji' ? (
              <span
                className={headerStyles.siteBannerMarker}
                {...editTargets?.marker}
              >
                {banner.marker.emoji}
              </span>
            ) : banner.marker.type === 'media' ? (
              <span
                className={`${headerStyles.siteBannerMarker} ${headerStyles.siteBannerMarkerAsset}`}
                {...editTargets?.marker}
              >
                <img
                  alt={banner.marker.imageAlt}
                  className={headerStyles.siteBannerMarkerImage}
                  height={banner.marker.image.height}
                  src={banner.marker.image.url}
                  width={banner.marker.image.width}
                />
              </span>
            ) : null}
          </span>
          <span className={headerStyles.siteBannerContent}>
            <span
              className={headerStyles.siteBannerMessage}
              {...editTargets?.message}
            >
              {banner.message}
            </span>
            {banner.ctaLabel ? (
              <span
                className={headerStyles.siteBannerCta}
                {...editTargets?.cta}
              >
                {banner.ctaLabel}
              </span>
            ) : null}
          </span>
          <span aria-hidden="true" />
        </span>
      </SiteRuntimeTargetTrigger>

      {banner.dismissible ? (
        <button
          aria-label={uiCopy.dismissBanner}
          className={headerStyles.siteBannerClose}
          onClick={() => {
            dismissBannerForSession(banner);
            setDismissed(true);
          }}
          type="button"
          {...editTargets?.dismiss}
        >
          <X aria-hidden="true" className={headerStyles.siteBannerCloseIcon} />
        </button>
      ) : null}
    </div>
  );
}
